// Audio layer for the simulator. All synthesised via the WebAudio API — no sample files.
//
// Tone tables tuned to IEC 60601-1-8 (medical equipment alarm signals) and Philips IntelliVue
// "Traditional" sound set (dominant in UK NHS). SpO2 pitch curve follows the Nellcor-style
// linear mapping (~9.6 Hz per 1% SpO2) validated in Loeb et al. 2016 (PubMed 27028772).
// LIFEPAK 15 / Zoll X Series charge tones approximated from the IFUs (200→1500 Hz sweep
// over 4 s, then pulsed 1500 Hz ready tone). Full citations in rag-sources/web-research.
//
// Each "alarm" tone meets IEC envelopes: fundamental 150-1000 Hz, ≥4 harmonics 300-4000 Hz
// within ±15 dB of fundamental, raised-cosine 10-100 ms rise/fall. We layer 3 oscillators
// (fundamental + 2nd + 3rd) per alarm pulse to produce the required harmonic richness.

export type AudioChannel = 'alarms' | 'pulse_ox' | 'defib' | 'voice';

interface AudioState {
  muted: boolean;
  volumes: Record<AudioChannel, number>;
  context: AudioContext | null;
  master: GainNode | null;
  channelGains: Record<AudioChannel, GainNode | null>;
  alarmInterval: number | null;
  pulseOxInterval: number | null;
  defibReadyInterval: number | null;
  lastAlarm: string | null;
}

const audioState: AudioState = {
  muted: false,
  volumes: { alarms: 0.5, pulse_ox: 0.3, defib: 0.5, voice: 0.9 },
  context: null,
  master: null,
  channelGains: { alarms: null, pulse_ox: null, defib: null, voice: null },
  alarmInterval: null,
  pulseOxInterval: null,
  defibReadyInterval: null,
  lastAlarm: null,
};

function ensureContext(): AudioContext | null {
  if (audioState.context) return audioState.context;
  if (typeof window === 'undefined') return null;
  const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  const ctx: AudioContext = new Ctor();
  audioState.context = ctx;
  const master = ctx.createGain();
  master.gain.value = audioState.muted ? 0 : 1;
  master.connect(ctx.destination);
  audioState.master = master;
  for (const ch of ['alarms', 'pulse_ox', 'defib', 'voice'] as AudioChannel[]) {
    const g = ctx.createGain();
    g.gain.value = audioState.volumes[ch];
    g.connect(master);
    audioState.channelGains[ch] = g;
  }
  return ctx;
}

export function setMuted(muted: boolean): void {
  audioState.muted = muted;
  if (audioState.master) audioState.master.gain.value = muted ? 0 : 1;
}
export function isMuted(): boolean { return audioState.muted; }
export function setChannelVolume(ch: AudioChannel, v: number): void {
  audioState.volumes[ch] = Math.max(0, Math.min(1, v));
  const gain = audioState.channelGains[ch];
  if (gain) gain.gain.value = audioState.volumes[ch];
}
export async function unlockAudio(): Promise<void> {
  const ctx = ensureContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') { try { await ctx.resume(); } catch { /* ignore */ } }
}

// =====================================================================================
// Harmonically-rich tone primitive — meets IEC 60601-1-8 ≥4-harmonics requirement.
// Layers fundamental + 2nd + 3rd partials (each at -6, -10, -14 dB respectively below
// peak) to produce the recognisably "monitor-shaped" pulse rather than a pure sine.
// =====================================================================================

interface ToneOpts {
  channel: AudioChannel;
  freq: number;
  durationMs: number;
  type?: OscillatorType;
  attackMs?: number;
  releaseMs?: number;
  gain?: number;
  freqEnd?: number;
  /** Add harmonic stack (2nd + 3rd partials). Off by default for "thin" tones (SpO2 beep). */
  harmonics?: boolean;
}

function tone(opts: ToneOpts): void {
  const ctx = ensureContext();
  if (!ctx || audioState.muted) return;
  if (ctx.state !== 'running') return;
  const channelGain = audioState.channelGains[opts.channel];
  if (!channelGain) return;

  const peak = opts.gain ?? 0.4;
  const attack = (opts.attackMs ?? 12) / 1000;
  const release = (opts.releaseMs ?? 40) / 1000;
  const dur = opts.durationMs / 1000;
  const now = ctx.currentTime;

  // Build partial stack: fundamental at peak, 2nd at -6dB (0.5x), 3rd at -12dB (0.25x)
  const partials = opts.harmonics
    ? [{ mult: 1, gain: 1.0 }, { mult: 2, gain: 0.5 }, { mult: 3, gain: 0.25 }]
    : [{ mult: 1, gain: 1.0 }];

  for (const p of partials) {
    const osc = ctx.createOscillator();
    osc.type = opts.type || 'sine';
    osc.frequency.setValueAtTime(opts.freq * p.mult, now);
    if (opts.freqEnd) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.freqEnd * p.mult), now + dur);
    }
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(peak * p.gain, now + attack);
    g.gain.setValueAtTime(peak * p.gain, now + Math.max(attack, dur - release));
    g.gain.linearRampToValueAtTime(0, now + dur);
    osc.connect(g);
    g.connect(channelGain);
    osc.start();
    osc.stop(now + dur + 0.05);
    osc.onended = () => { try { osc.disconnect(); g.disconnect(); } catch { /* ignore */ } };
  }
}

// =====================================================================================
// Alarms — IEC 60601-1-8 patterns
// =====================================================================================
//
// HIGH (Crisis/Red): 10 pulses in two halves of 5, td=100ms, internal gaps 70/70/150/70 ms
//                    (giving 3-2 rhythm inside each half), 350 ms gap between halves,
//                    burst-to-burst 5 s.
// MED (Yellow):      3 pulses, td=200ms, 150 ms internal gap, 25 s burst-to-burst.
// LOW (Cyan):        2 pulses, td=250ms, 250 ms gap, 30 s burst-to-burst.
// INOP (technical):  2 short "bing-bing" at 660 Hz, distinct from patient alarms.

type AlarmStep = { freq: number; durationMs: number; gapAfterMs: number; type?: OscillatorType; gain?: number; freqEnd?: number; harmonics?: boolean };
interface AlarmPattern { steps: AlarmStep[]; loopMs: number; }

// Build an IEC high-priority burst with the canonical 3-2 / 3-2 cadence.
// `freq` is the fundamental — 880 Hz for life-threat (asystole/VF/VT/apnoea),
// 660 Hz for "high but not crisis" (BP critical, SpO2 critical).
function makeHighPriority(freq: number): AlarmPattern {
  const td = 100, gShort = 70, gLong = 150, gHalf = 350;
  // first half: pulse, pulse, pulse (long gap), pulse, pulse
  const step = (gap: number): AlarmStep => ({ freq, durationMs: td, gapAfterMs: gap, type: 'square', gain: 0.5, harmonics: true });
  return {
    steps: [
      step(gShort), step(gShort), step(gLong),    // 3 pulses
      step(gShort), step(gHalf),                  // 2 pulses + half-burst gap
      step(gShort), step(gShort), step(gLong),    // 3 pulses
      step(gShort), step(0),                      // 2 pulses
    ],
    loopMs: 5000,
  };
}

function makeMedPriority(freq: number): AlarmPattern {
  const td = 200, g = 150;
  const step: AlarmStep = { freq, durationMs: td, gapAfterMs: g, type: 'square', gain: 0.42, harmonics: true };
  return {
    steps: [
      { ...step },
      { ...step },
      { ...step, gapAfterMs: 0 },
    ],
    loopMs: 25000,
  };
}

function makeLowPriority(freq: number): AlarmPattern {
  const td = 250, g = 250;
  return {
    steps: [
      { freq, durationMs: td, gapAfterMs: g, type: 'triangle', gain: 0.35, harmonics: false },
      { freq, durationMs: td, gapAfterMs: 0, type: 'triangle', gain: 0.35, harmonics: false },
    ],
    loopMs: 30000,
  };
}

// "Ventilation category" preamble — short breath-shaped 5-pulse melody at apnoea pitch.
// Played once at the start of an apnoea alarm before the standard high-priority burst.
function makeVentilationCategory(): AlarmPattern {
  // Inhale (rising) — pause — exhale (falling) shape, 5 pulses total.
  const td = 150, g = 100;
  return {
    steps: [
      { freq: 440, freqEnd: 587, durationMs: td, gapAfterMs: g, type: 'triangle', gain: 0.4, harmonics: true },
      { freq: 587, durationMs: td, gapAfterMs: g, type: 'triangle', gain: 0.4, harmonics: true },
      { freq: 523, durationMs: td, gapAfterMs: g, type: 'triangle', gain: 0.4, harmonics: true },
      { freq: 466, durationMs: td, gapAfterMs: g, type: 'triangle', gain: 0.4, harmonics: true },
      { freq: 392, durationMs: td, gapAfterMs: 0, type: 'triangle', gain: 0.4, harmonics: true },
    ],
    loopMs: 0, // one-shot preamble; the controller chains a high-priority pattern after
  };
}

const ALARM_PATTERNS: Record<string, AlarmPattern> = {
  // VF / pVT / torsades — Crisis/Red. IEC high-priority 10-pulse @ 880 Hz square.
  vf: makeHighPriority(880),

  // Asystole — same Crisis/Red pattern but at lower 660 Hz fundamental so it sounds
  // distinct from VF without violating IEC. Clinically both are "red" — but real
  // monitors use the cardiovascular category identifier to differentiate. Keeping
  // them separate by pitch is the easy-to-recognise compromise.
  asystole: makeHighPriority(660),

  // PEA — same as asystole pitch-wise (both non-shockable). UK nurses recognise
  // "the lower one" as non-shockable; the higher VF/VT pitch as the "shock now" alarm.
  pea: makeHighPriority(660),

  // SpO2 low — Medium priority 3-pulse @ 523 Hz (C5).
  spo2_low: makeMedPriority(523),

  // BP critical low — High priority (but at 660 Hz, not VF's 880 Hz).
  bp_low: makeHighPriority(660),

  // Apnoea — ventilation-category preamble then a high-priority burst.
  // The controller plays the preamble once on entry and then chains apnoea_loop.
  apnoea_preamble: makeVentilationCategory(),
  apnoea_loop: makeHighPriority(740),

  // Generic medium-priority technical-not-clinical alarm (BP cuff fail, etc).
  med_yellow: makeMedPriority(523),

  // INOP — leads off / sensor disconnected. Two short pulses at 660 Hz. UK staff
  // hear this as "the lower 'bing-bing'" — distinctly non-patient-threatening.
  inop: {
    steps: [
      { freq: 660, durationMs: 200, gapAfterMs: 200, type: 'square', gain: 0.35, harmonics: true },
      { freq: 660, durationMs: 200, gapAfterMs: 0, type: 'square', gain: 0.35, harmonics: true },
    ],
    loopMs: 30000,
  },

  // Low-priority advisory (settings change reminder, etc).
  low_cyan: makeLowPriority(440),
};

function playPattern(p: AlarmPattern): void {
  let t = 0;
  for (const step of p.steps) {
    const s = step;
    setTimeout(() => tone({
      channel: 'alarms',
      freq: s.freq,
      durationMs: s.durationMs,
      type: s.type || 'sine',
      gain: s.gain ?? 0.45,
      freqEnd: s.freqEnd,
      attackMs: 12,    // raised-cosine 10-100 ms per IEC
      releaseMs: Math.min(40, s.durationMs / 3),
      harmonics: s.harmonics,
    }), t);
    t += step.durationMs + step.gapAfterMs;
  }
}

/**
 * Start (or change) the recurring monitor alarm. Pass null to silence.
 * Same alarm key = no restart.
 */
export function setAlarm(kind: keyof typeof ALARM_PATTERNS | null): void {
  if (audioState.lastAlarm === kind) return;
  audioState.lastAlarm = kind;
  if (audioState.alarmInterval !== null) {
    clearInterval(audioState.alarmInterval);
    audioState.alarmInterval = null;
  }
  if (!kind) return;
  const pattern = ALARM_PATTERNS[kind];
  if (!pattern) return;

  // Apnoea: play ventilation-category preamble once, then loop the high-priority pattern.
  if (kind === 'apnoea_preamble') {
    playPattern(pattern);
    setTimeout(() => setAlarm('apnoea_loop' as any), 1500);
    return;
  }

  const play = () => playPattern(pattern);
  play();
  audioState.alarmInterval = window.setInterval(play, pattern.loopMs) as unknown as number;
}

// =====================================================================================
// Pulse oximeter beep
// =====================================================================================
//
// Nellcor-style linear pitch curve: ~9.6 Hz drop per 1% SpO2, sine wave, 80 ms beeps.
// 100% → 880 Hz (A5);  90% → 784 Hz (G5);  80% → 698 Hz (F5);  70% → 622 Hz (D#5);
// 50% → 494 Hz (B4).  This is the curve UK nurses' ears are tuned to.
// Source: Loeb et al. 2016 "Sounds of Desaturation" PMC9767428.

function spo2Frequency(spo2_pct: number): number {
  // Linear ~9.6 Hz/%: anchor at 880 Hz @ 100%, slope -9.6 Hz/%.
  const slope = 9.6;
  const anchor = 880;
  const freq = anchor - (100 - spo2_pct) * slope;
  return Math.max(280, Math.min(1000, freq)); // IEC envelope clamp
}

export function setPulseOx(hr_bpm: number, spo2_pct: number): void {
  if (audioState.pulseOxInterval !== null) {
    clearInterval(audioState.pulseOxInterval);
    audioState.pulseOxInterval = null;
  }
  if (hr_bpm <= 0 || spo2_pct <= 0) return;
  const period_ms = 60_000 / hr_bpm;
  const freq = spo2Frequency(spo2_pct);
  const beep = () => tone({
    channel: 'pulse_ox',
    freq,
    durationMs: 80,
    type: 'sine',
    gain: 0.25,
    attackMs: 8,
    releaseMs: 30,
    harmonics: false,
  });
  beep();
  audioState.pulseOxInterval = window.setInterval(beep, period_ms) as unknown as number;
}

// =====================================================================================
// Defibrillator sounds — LIFEPAK 15 / Zoll X Series profile
// =====================================================================================
//
// Charging: 200 Hz → 1500 Hz exponential sweep over ~4 s (real LIFEPAK 15 reaches 360 J
// in ≤7 s; we use 4 s for the typical 150-200 J training setting). Sawtooth wave for
// the "capacitor whine" character.
//
// Charge-ready: continuous 1500 Hz pulsed at 4 Hz until the SHOCK button is pressed.
// We auto-stop the ready pulse after 12 s (matching the typical safety-timeout window).
//
// Shock delivered: brief relay-style 100 Hz thunk plus a wideband click. The patient
// stimulus itself is silent on the device — the click is just the relay.

export function defibCharge(targetJoules = 200): void {
  const ctx = ensureContext();
  if (!ctx || audioState.muted) return;
  if (ctx.state !== 'running') return;
  const channelGain = audioState.channelGains['defib'];
  if (!channelGain) return;
  // Stop any in-flight "ready" pulse loop from a previous charge cycle
  if (audioState.defibReadyInterval !== null) {
    clearInterval(audioState.defibReadyInterval);
    audioState.defibReadyInterval = null;
  }

  const chargeDuration = 4.0; // seconds, fits LIFEPAK 15 ≤7 s spec for 200 J
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(200, now);
  osc.frequency.exponentialRampToValueAtTime(1500, now + chargeDuration);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(0.18, now + 0.15);
  g.gain.linearRampToValueAtTime(0.35, now + chargeDuration - 0.1);
  g.gain.linearRampToValueAtTime(0, now + chargeDuration + 0.05);
  osc.connect(g); g.connect(channelGain);
  osc.start();
  osc.stop(now + chargeDuration + 0.1);
  osc.onended = () => { try { osc.disconnect(); g.disconnect(); } catch { /* ignore */ } };

  // Charge-ready: pulsed 1500 Hz at 4 Hz, auto-stops after 12 s (or on shock delivery)
  setTimeout(() => {
    const readyBeep = () => tone({ channel: 'defib', freq: 1500, durationMs: 100, type: 'square', gain: 0.40, harmonics: true, attackMs: 8, releaseMs: 30 });
    readyBeep();
    audioState.defibReadyInterval = window.setInterval(readyBeep, 250) as unknown as number;
    // Safety auto-stop
    setTimeout(() => {
      if (audioState.defibReadyInterval !== null) {
        clearInterval(audioState.defibReadyInterval);
        audioState.defibReadyInterval = null;
      }
    }, 12_000);
  }, chargeDuration * 1000 + 50);

  void targetJoules;
}

export function defibDischarge(): void {
  // Stop the charge-ready pulse the instant shock is delivered
  if (audioState.defibReadyInterval !== null) {
    clearInterval(audioState.defibReadyInterval);
    audioState.defibReadyInterval = null;
  }
  // Relay-style 100 Hz thunk (low square pulse) + brief wideband click via filtered noise
  tone({ channel: 'defib', freq: 100, durationMs: 50, type: 'square', gain: 0.7, attackMs: 1, releaseMs: 20, harmonics: true });
  // Pink-noise-style click via stacked decaying sines (approximation — WebAudio has no
  // built-in pink noise; this is good enough for a relay snap)
  setTimeout(() => {
    tone({ channel: 'defib', freq: 1200, durationMs: 30, type: 'square', gain: 0.4, attackMs: 1, releaseMs: 15 });
    tone({ channel: 'defib', freq: 2400, durationMs: 25, type: 'sawtooth', gain: 0.25, attackMs: 1, releaseMs: 12 });
  }, 30);
}

// =====================================================================================
// Pacer capture click
// =====================================================================================
//
// During transcutaneous pacing each delivered impulse produces an audible click on
// the device (1 kHz, ~30 ms, square wave). Programmable rate 30-180 BPM on LIFEPAK 15
// + Zoll X Series. We use the same call shape as setPulseOx but at the pacing rate.

let pacerInterval: number | null = null;
export function setPacerCapture(rate_bpm: number | 0): void {
  if (pacerInterval !== null) { clearInterval(pacerInterval); pacerInterval = null; }
  if (rate_bpm <= 0) return;
  const period_ms = 60_000 / rate_bpm;
  const click = () => tone({ channel: 'defib', freq: 1000, durationMs: 30, type: 'square', gain: 0.35, attackMs: 2, releaseMs: 12, harmonics: false });
  click();
  pacerInterval = window.setInterval(click, period_ms) as unknown as number;
}

// =====================================================================================
// TTS for virtual-team responses (browser SpeechSynthesis API)
// =====================================================================================

export function speak(text: string, opts: { voice?: 'team_member' | 'team_leader' } = {}): void {
  if (audioState.muted) return;
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  const utter = new SpeechSynthesisUtterance(text);
  utter.volume = audioState.volumes.voice;
  utter.rate = 1.0;
  utter.pitch = opts.voice === 'team_member' ? 1.0 : 0.95;
  const voices = window.speechSynthesis.getVoices();
  if (voices.length > 0) {
    const gb = voices.find((v) => v.lang.startsWith('en-GB')) || voices.find((v) => v.lang.startsWith('en')) || voices[0];
    if (gb) utter.voice = gb;
  }
  window.speechSynthesis.speak(utter);
}

export function stopSpeaking(): void {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
}
